import { useCallback, useState } from 'react';

import { AuthProvider, authStatus, TokenModel } from '@picsart/growth-rc/AuthContext';
import { getCookie } from '@picsart/rc/cookie';
import { authorizationHeader } from '@picsart/growth-rc/services/auth';
import useMount from '@picsart/rc/useMount';

import endpointConstructor from 'utils/shared/endpointConstructor';
import { setUserInfo } from 'store/actions/userInfo';
import fetcher from 'utils/shared/fetcher';
import { usePublish } from 'store';
import isomorphicUseEffect from 'utils/isomorphicUseEffect';

export interface AuthWrapperProps {
  children: any;
  userData: any;
  initialTokenData: TokenModel;
}

function AuthWrapper({ children, userData, initialTokenData }: AuthWrapperProps) {
  const [tokenData, setTokenData] = useState<TokenModel>(initialTokenData);
  const [status, setStatus] = useState<authStatus>(authStatus.pending);
  const [user, setUserChange] = useState(userData);

  const dispatch = usePublish();

  const onUserChange = useCallback(
    data => {
      if (tokenData?.access_token) {
        if (data?.userData && data?.userInfo) {
          setUserChange(data?.userData);
          dispatch(
            setUserInfo({
              userId: data?.userData.id,
              followingUserIds: data?.userInfo.followingUserIds,
              followingTags: data?.userInfo.followingTags,
            }),
          );
        } else if (data) {
          setUserChange(data);

          const deviceid = getCookie('paa-did') || null;
          fetcher(
            endpointConstructor('user', '', {
              deviceid,
              accessToken: tokenData?.access_token,
            }),
          )
            .then(({ userInfo }) => {
              dispatch(
                setUserInfo({
                  userId: data.id,
                  followingUserIds: userInfo.followingUserIds,
                  followingTags: userInfo.followingTags,
                }),
              );
            })
            .catch(error => {
              console.error('::: error :::', error);
              setStatus(authStatus.failed);
            });
        }
      }
    },
    [dispatch, tokenData?.access_token],
  );

  useMount(() => {
    if (initialTokenData) {
      setTokenData(initialTokenData);
      setStatus(authStatus.resolved);
    }
    if (userData) {
      onUserChange(userData);
    }
  });

  isomorphicUseEffect(() => {
    if (tokenData?.access_token && !user) {
      fetcher(endpointConstructor('user', '', { accessToken: tokenData?.access_token }), {
        headers: authorizationHeader(tokenData?.access_token),
      })
        .then(response => {
          if (response) {
            onUserChange(response);
            setStatus(authStatus.resolved);
          }
        })
        .catch(error => {
          console.error('::: error :::', error);
          setStatus(authStatus.failed);
        });
    }
  }, [onUserChange, tokenData?.access_token, user, userData]);

  return (
    <AuthProvider
      user={user}
      onUserChange={onUserChange}
      basePath={'/'}
      status={status}
      onTokenChange={setTokenData}
      onStatusChange={setStatus}
      tokenData={tokenData}
      isSsr
    >
      {children}
    </AuthProvider>
  );
}

export default AuthWrapper;
